import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {
  Observable,
  BehaviorSubject,
  catchError,
  tap,
  forkJoin,
  map,
  from,
} from 'rxjs';
import { environment } from '../../../../../environments/environment';
import { ErrorHandlingService } from '../../../../core/services/error-handling.service';
import {
  AttachmentResponse,
  MediaTypeResponse,
  UploadResponse,
  Attachment,
  AttachmentResponseWithPlanType,
  AttachmentResponseByPlanTypeFolder,
} from '../../insights.interface';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import {
  UpdateAttachmentPayload,
  UploadAttachmentPayload,
} from './questionnaires-form/questionnaires.interface';

@Injectable({
  providedIn: 'root',
})
export class StudentAttachmentService {
  private readonly BASE_URL = environment.apiUrl;
  private readonly http = inject(HttpClient);
  private readonly errorHandler = inject(ErrorHandlingService);

  private readonly attachmentsSubject =
    new BehaviorSubject<AttachmentResponse | null>(null);
  readonly attachments$ = this.attachmentsSubject.asObservable();

  uploadAttachment(
    payload: UploadAttachmentPayload
  ): Observable<UploadResponse> {
    const formData = new FormData();
    formData.append('CaseId', payload.caseId.toString());
    formData.append('folderId', payload.folderId.toString());
    formData.append(
      'planTypeId',
      payload.planTypeId ? payload.planTypeId.toString() : ''
    );
    formData.append('Name', payload.name);
    if (payload.caseAttachmentId) {
      formData.append(
        'caseAttachmentId',
        payload.caseAttachmentId ? payload.caseAttachmentId.toString() : ''
      );
    }
    formData.append('Description', payload.description);
    formData.append('MediaTypeId', payload.mediaTypeId);
    formData.append('File', payload.file);
    if (payload.modifiedFields && payload.modifiedFields.length > 0) {
      formData.append(
        '_modifiedFields',
        JSON.stringify(payload.modifiedFields)
      );
    }
    payload.isQuestionnairePDF = payload.isQuestionnairePDF || false;

    formData.append(
      'isQuestionnairePDF',
      payload.isQuestionnairePDF.toString()
    );
    formData.append(
      'isDraftQuestionnaire',
      payload.isDraftQuestionnaire.toString()
    );
    formData.append(
      'draftStateQuestionnairePlanTypeId',
      payload.draftStateQuestionnairePlanTypeId != null
        ? payload.draftStateQuestionnairePlanTypeId.toString()
        : ''
    );

    return this.http
      .post<UploadResponse>(`${this.BASE_URL}/CaseAttachment/upload`, formData)
      .pipe(
        tap(() => this.refreshAttachments(payload.caseId)),
        catchError(error => this.errorHandler.handleError(error))
      );
  }

  getAttachments(caseId: number): Observable<AttachmentResponse> {
    return this.http
      .get<AttachmentResponse>(`${this.BASE_URL}/CaseAttachment/list`, {
        params: { caseId: caseId.toString() },
      })
      .pipe(
        tap(response => this.attachmentsSubject.next(response)),
        catchError(error => this.errorHandler.handleError(error))
      );
  }

  getAttachmentsWithPlanTypeId(
    caseId: number
  ): Observable<AttachmentResponseWithPlanType> {
    return this.http
      .get<AttachmentResponseWithPlanType>(
        `${this.BASE_URL}/CaseAttachment/get-list-by-plantypeid-caseid`,
        {
          params: { caseId: caseId.toString() },
        }
      )
      .pipe(
        tap(response => {
          return response;
        }),
        catchError(error => this.errorHandler.handleError(error))
      );
  }

  getAttachmentsByPlanTypeFolder(
    caseId: number
  ): Observable<AttachmentResponseByPlanTypeFolder> {
    return this.http
      .get<AttachmentResponseByPlanTypeFolder>(
        `${this.BASE_URL}/CaseAttachment/get-list-by-plantype-folder-caseid`,
        {
          params: { caseId: caseId.toString() },
        }
      )
      .pipe(catchError(error => this.errorHandler.handleError(error)));
  }

  updateAttachment(
    payload: UpdateAttachmentPayload
  ): Observable<UploadResponse> {
    const formData = new FormData();
    formData.append('CaseId', payload.caseId.toString());
    formData.append('folderId', payload.folderId.toString());
    formData.append(
      'planTypeId',
      payload.planTypeId ? payload.planTypeId.toString() : ''
    );
    formData.append('Name', payload.name);
    formData.append('Description', payload.description);
    formData.append('MediaTypeId', payload.mediaTypeId.toString());
    formData.append('File', payload.file);
    formData.append('blobName', payload.file.name);
    if (payload.caseAttachmentId) {
      formData.append(
        'caseAttachmentId',
        payload.caseAttachmentId ? payload.caseAttachmentId.toString() : ''
      );
    }
    if (!payload.isQuestionnairePDF) {
      payload.isQuestionnairePDF = false;
    }
    formData.append(
      'isQuestionnairePDF',
      payload.isQuestionnairePDF.toString()
    );
    formData.append(
      'isDraftQuestionnaire',
      payload.isDraftQuestionnaire.toString()
    );
    formData.append(
      'draftStateQuestionnairePlanTypeId',
      payload.draftStateQuestionnairePlanTypeId != null
        ? payload.draftStateQuestionnairePlanTypeId.toString()
        : ''
    );
    if (payload._modifiedFields && payload._modifiedFields.length > 0) {
      formData.append(
        '_modifiedFields',
        JSON.stringify(payload._modifiedFields)
      );
    }

    return this.http
      .put<UploadResponse>(`${this.BASE_URL}/CaseAttachment/update`, formData, {
        params: { id: payload.id.toString() },
      })
      .pipe(
        tap(() => this.refreshAttachments(payload.caseId)),
        catchError(error => this.errorHandler.handleError(error))
      );
  }

  finalizeDraftState(caseId: number): Observable<UploadResponse> {
    return this.http
      .post<UploadResponse>(
        `${this.BASE_URL}/CaseAttachment/finish`,
        {
          draftStateQuestionnairePlanTypeId: null,
          isDraftQuestionnaire: false,
        },
        { params: { caseId: caseId.toString() } }
      )
      .pipe(catchError(error => this.errorHandler.handleError(error)));
  }

  deleteAttachment(id: number): Observable<UploadResponse> {
    return this.http
      .delete<UploadResponse>(`${this.BASE_URL}/CaseAttachment/${id}`)
      .pipe(catchError(error => this.errorHandler.handleError(error)));
  }

  getMediaTypes(searchTerm = ''): Observable<MediaTypeResponse> {
    return this.http
      .get<MediaTypeResponse>(`${this.BASE_URL}/ApplicationLookup`, {
        params: {
          TypeName: 'MediaType',
          SearchTerm: searchTerm,
        },
      })
      .pipe(catchError(error => this.errorHandler.handleError(error)));
  }

  private getExtensionFromMediaType(mediaTypeId: number): string {
    const mediaTypeMap: Record<number, string> = {
      1: '.pdf', // PDF files
      2: '.mp4', // Video files
      3: '.mp3', // Audio files
      4: '.docx', // Word documents
      5: '.xlsx', // Excel files
      6: '.pptx', // PowerPoint files
      7: '.wav', // Audio files
      8: '.tif', // Image files
      9: '.m4v', // Video files
      10: '.m4a', // Audio files
      11: '.ppt', // PowerPoint files (older format)
      12: '.xls', // Excel files (older format)
      13: '.doc', // Word documents (older format)
      14: '.tiff', // Image files
    };

    return mediaTypeMap[mediaTypeId] || '';
  }

  downloadAttachmentBlob(url: string): Observable<Blob> {
    const isAzureBlobUrl = url.includes('blob.core.windows.net');

    if (isAzureBlobUrl) {
      return from(
        fetch(url).then(response => {
          if (!response.ok) {
            throw new Error(
              `Failed to download file: ${response.status} ${response.statusText}`
            );
          }
          return response.blob();
        })
      ).pipe(catchError(error => this.errorHandler.handleError(error)));
    } else {
      return this.http
        .get(url, { responseType: 'blob' })
        .pipe(catchError(error => this.errorHandler.handleError(error)));
    }
  }

  downloadAttachmentsAsZip(
    attachments: Attachment[],
    zipFileName = 'attachments.zip'
  ): Observable<void> {
    const downloadRequests = attachments
      .filter(
        attachment => attachment.fileUrl && attachment.fileUrl.trim() !== ''
      )
      .map(attachment =>
        this.downloadAttachmentBlob(attachment.fileUrl).pipe(
          map(blob => ({ blob, name: attachment.name, attachment }))
        )
      );

    return forkJoin(downloadRequests).pipe(
      map(responses => {
        const zip = new JSZip();
        const usedFileNames = new Set<string>();

        responses.forEach(({ blob, name, attachment }) => {
          const extension = this.determineFileExtension(attachment);
          const finalFileName = this.generateUniqueFileName(
            name,
            extension,
            usedFileNames
          );

          usedFileNames.add(finalFileName.toLowerCase());
          zip.file(finalFileName, blob);
        });

        zip.generateAsync({ type: 'blob' }).then((content: Blob) => {
          saveAs(content, zipFileName);
        });
      }),
      catchError(error => this.errorHandler.handleError(error))
    );
  }

  private determineFileExtension(attachment: Attachment): string {
    let extension = this.getExtensionFromMediaType(attachment.mediaTypeId);

    if (!extension && attachment.fileUrl.includes('blob.core.windows.net')) {
      extension = this.extractExtensionFromBlobUrl(attachment.fileUrl);
    }

    return extension;
  }

  private extractExtensionFromBlobUrl(url: string): string {
    const urlPath = url.split('?')[0];
    const pathParts = urlPath.split('/');
    const fileName = pathParts.at(-1) || '';

    return this.findExtensionInFileName(fileName);
  }

  private findExtensionInFileName(fileName: string): string {
    const commonExtensions = [
      'mp4',
      'm4v',
      'm4a',
      'wav',
      'pptx',
      'ppt',
      'xlsx',
      'xls',
      'docx',
      'doc',
      'mp3',
      'pdf',
      'tif',
      'tiff',
    ];
    const lowerFileName = fileName.toLowerCase();

    for (const ext of commonExtensions) {
      if (lowerFileName.endsWith(`.${ext}`)) {
        return `.${ext}`;
      }
    }

    return this.extractGenericExtension(fileName);
  }

  private extractGenericExtension(fileName: string): string {
    if (!fileName.includes('.')) {
      return '';
    }

    const lastDotIndex = fileName.lastIndexOf('.');
    const possibleExt = fileName.substring(lastDotIndex + 1).toLowerCase();

    if (possibleExt.length <= 5 && !possibleExt.includes(' ')) {
      return `.${possibleExt}`;
    }

    return '';
  }

  private generateUniqueFileName(
    name: string,
    extension: string,
    usedFileNames: Set<string>
  ): string {
    let cleanFileName = name.replaceAll(/[/\\]/g, '_');

    if (
      extension &&
      !cleanFileName.toLowerCase().endsWith(extension.toLowerCase())
    ) {
      cleanFileName = `${cleanFileName}${extension}`;
    }

    return this.ensureUniqueFileName(cleanFileName, extension, usedFileNames);
  }

  private ensureUniqueFileName(
    fileName: string,
    extension: string,
    usedFileNames: Set<string>
  ): string {
    let finalFileName = fileName;
    let counter = 1;

    while (usedFileNames.has(finalFileName.toLowerCase())) {
      const nameWithoutExt = extension
        ? fileName.slice(0, -extension.length)
        : fileName;
      finalFileName = `${nameWithoutExt}_${counter}${extension}`;
      counter++;
    }

    return finalFileName;
  }

  private refreshAttachments(caseId: number): void {
    this.getAttachments(caseId).subscribe();
  }
}
